import React, { useEffect, useState } from 'react';
import './Roles.css';
import privateApi from '../../apis/privateApi';
import Spinner from '../../components/Spinner/Spinner';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';

import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEdit, FiTrash2 } from "react-icons/fi";

const Roles = () => {
    const navigate = useNavigate();

    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [filters, setFilters] = useState({
        name: '',
        level: ''
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalPages: 1,
        totalRecords: 0
    });

    // Delete states
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState("");
    const [deleteId, setDeleteId] = useState(null);

    const generateToken = () =>
        Math.random().toString(36).substring(2, 8).toUpperCase();

    // Fetch Roles
    const fetchRoles = async () => {
        try {
            setLoading(true);
            setError('');

            const query = new URLSearchParams({
                ...filters,
                page: pagination.page,
                limit: pagination.limit
            });

            const { data } = await privateApi.get(`/api/roles?${query}`);

            if (data.success) {
                setRoles(data.data);
                setPagination(prev => ({
                    ...prev,
                    totalPages: data.pagination?.pages || 1,
                    totalRecords: data.pagination?.total || data.data.length
                }));
            } else {
                setRoles([]);
                setError('No records found');
            }
        } catch (err) {
            setRoles([]);
            setError(err.response?.data?.message || 'Failed to fetch roles.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initialFetchRoles = async () => {
            try {
                setLoading(true);
                setError('');

                const query = new URLSearchParams({
                    ...filters,
                    page: pagination.page,
                    limit: pagination.limit
                });

                const { data } = await privateApi.get(`/api/roles?${query}`);

                if (data.success) {
                    setRoles(data.data);
                    setPagination(prev => ({
                        ...prev,
                        totalPages: data.pagination?.pages || 1,
                        totalRecords: data.pagination?.total || data.data.length
                    }));
                } else {
                    setRoles([]);
                    setError('No records found');
                }
            } catch (err) {
                setRoles([]);
                setError(err.response?.data?.message || 'Failed to fetch roles.');
            } finally {
                setLoading(false);
            }
        };

        initialFetchRoles();
    }, [filters, pagination.page, pagination.limit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchRoles();
    };

    const handleLimitChange = (e) => {
        const limit = parseInt(e.target.value, 10);
        setPagination(prev => ({ ...prev, limit, page: 1 }));
    };

    const nextPage = () => {
        if (pagination.page < pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
        }
    };

    const prevPage = () => {
        if (pagination.page > 1) {
            setPagination(prev => ({ ...prev, page: prev.page - 1 }));
        }
    };

    // -------------------
    // Delete logic
    // -------------------
    const handleDeleteClick = (id) => {
        const token = generateToken();
        setDeleteId(id);
        setConfirmToken(token);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async (input) => {
        if (input !== confirmToken) {
            alert("Incorrect confirmation code.");
            setConfirmOpen(false);
            return;
        }

        try {
            await privateApi.delete(`/api/roles/${deleteId}`);
            fetchRoles();
        } catch (err) {
            alert(err.response?.data?.message || "Delete failed.");
        } finally {
            setConfirmOpen(false);
            setDeleteId(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmOpen(false);
        setDeleteId(null);
    };

    return (
        <div className="roles">
            <header className="roles-header">
                <h2>Roles</h2>
                <div className='roles-action-btns'>
                    <Link to={"/roles/create"} className="roles-create-btn">Create Role</Link>
                    <button className="roles-back-btn" onClick={() => navigate(-1)}>Back</button>
                </div>
            </header>

            {/* Filters */}
            <div className="roles-filters">
                <input
                    name="name"
                    placeholder="Role Name"
                    value={filters.name}
                    onChange={handleChange}
                />

                <input
                    name="level"
                    placeholder="Level"
                    type="number"
                    value={filters.level}
                    onChange={handleChange}
                />

                <button onClick={applyFilters}>Search</button>
            </div>

            {/* Limit Selector */}
            <div className="roles-limit-selector">
                <label>
                    Show per page:
                    <select value={pagination.limit} onChange={handleLimitChange}>
                        <option value={10}>10</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </label>
            </div>

            {/* Table */}
            <div className="roles-table-wrapper">
                {loading ? (
                    <Spinner loadingInfo="Loading Roles..." />
                ) : error ? (
                    <p className="roles-error">{error}</p>
                ) : roles.length === 0 ? (
                    <p>No roles found.</p>
                ) : (
                    <table className="roles-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Level</th>
                                <th>Allowed Budget Levels</th>
                                <th>Permissions</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map(role => (
                                <tr key={role._id}>
                                    <td>{role.name}</td>
                                    <td>{role.description || '-'}</td>
                                    <td>{role.level}</td>
                                    <td>{role.allowedBudgetLevels?.map(p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ')}</td>
                                    {/* <td>
                                        {role.permissions?.map(p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ')}
                                    </td> */}
                                    <td title={role.permissions?.map(p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ') || '-'}>
                                        {(() => {
                                            const perms = role.permissions
                                                ?.map(p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                                                .join(', ') || '-';
                                            const maxLength = 50;
                                            return perms.length > maxLength ? perms.slice(0, maxLength) + '...' : perms;
                                        })()}
                                    </td>
                                    <td className="roles-actions">
                                        <Link to="/roles/detail" state={{ role }}>
                                            <FiEye />
                                        </Link>

                                        <Link to="/roles/edit" state={{ role }}>
                                            <FiEdit />
                                        </Link>

                                        <button
                                            onClick={() => handleDeleteClick(role._id)}
                                            className="delete-btn"
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {roles.length > 0 && (
                <div className="roles-pagination">
                    <button onClick={prevPage} disabled={pagination.page === 1}>
                        Prev
                    </button>

                    <span>
                        Page {pagination.page} of {pagination.totalPages}
                    </span>

                    <button
                        onClick={nextPage}
                        disabled={pagination.page === pagination.totalPages}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Confirm Delete */}
            <ConfirmDialog
                show={confirmOpen}
                token={confirmToken}
                action="You are about to delete this role"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default Roles;